/**
 * Essentials Library — admin-managed catalog of standard essential items.
 *
 * Items are grouped under user-defined categories (free to create, rename,
 * reorder, delete). Each row is just the essential's name; clicking opens
 * an edit dialog where you can rename, change category, add notes, and
 * manage default purchase links.
 *
 * Read-only for non-admin users (controls are hidden, click still opens
 * the dialog in disabled mode? — keep it simple: row click is disabled).
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Plus, Trash2, Search, Lock, ChevronDown, ChevronRight, Pencil, Link2,
  StickyNote, Copy,
} from 'lucide-react';
import {
  getFurnitureEssentialLinks,
  upsertFurnitureEssentialLinks,
  getFurnitureEssentialCategories,
  createFurnitureEssentialCategory,
  renameFurnitureEssentialCategory,
  deleteFurnitureEssentialCategory,
} from '@/lib/api';
import { EssentialEditDialog } from './EssentialEditDialog';

const UNCATEGORIZED = '__uncat__';

export default function EssentialLinksManager({ readOnly }) {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);          // entry being edited
  const [collapsed, setCollapsed] = useState({});         // { catKey: bool }
  const [renamingCat, setRenamingCat] = useState(null);   // category id
  const [catNameDraft, setCatNameDraft] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [newEssentialName, setNewEssentialName] = useState('');

  const load = async () => {
    // IMPORTANT: never flip `loading` on reload — it would unmount the tree
    // and reset the scroll position. Initial mount handles `loading` in
    // the useEffect below.
    try {
      const [es, cs] = await Promise.all([
        getFurnitureEssentialLinks(),
        getFurnitureEssentialCategories(),
      ]);
      setEntries(es);
      setCategories(cs);
    } catch {
      toast.error('Could not load essentials');
    }
  };

  useEffect(() => {
    (async () => { await load(); setLoading(false); })();
  }, []);

  // ── Group entries by category ──────────────────────────────
  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const visible = q
      ? entries.filter(e => e.name.toLowerCase().includes(q) || (e.notes || '').toLowerCase().includes(q))
      : entries;

    // Bucket by category name. Preserve admin-defined order, append
    // categories that exist on entries but not in the catalog (legacy data),
    // and put Uncategorized last.
    const known = new Map();
    categories.forEach(c => known.set(c.name, []));
    const orphanCats = new Map();
    const uncat = [];
    for (const e of visible) {
      const cat = e.category;
      if (!cat) { uncat.push(e); continue; }
      if (known.has(cat)) known.get(cat).push(e);
      else {
        if (!orphanCats.has(cat)) orphanCats.set(cat, []);
        orphanCats.get(cat).push(e);
      }
    }

    const sortedKnown = categories
      .map(c => ({ key: c.id, id: c.id, name: c.name, items: known.get(c.name) || [] }));
    const sortedOrphans = [...orphanCats.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, items]) => ({ key: `orphan-${name}`, id: null, name, items }));
    const out = [...sortedKnown, ...sortedOrphans];
    if (uncat.length || !q) {
      out.push({ key: UNCATEGORIZED, id: null, name: 'Uncategorized', items: uncat, isUncategorized: true });
    }
    // Sort items alphabetically within each group
    for (const g of out) g.items.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  }, [entries, categories, filter]);

  // ── Mutators ───────────────────────────────────────────────
  const addCategory = async () => {
    const n = newCatName.trim();
    if (!n) return;
    try {
      await createFurnitureEssentialCategory(n);
      setNewCatName('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create category');
    }
  };

  const saveCatName = async (cat) => {
    const next = catNameDraft.trim();
    if (!next || next === cat.name) {
      setRenamingCat(null);
      return;
    }
    try {
      await renameFurnitureEssentialCategory(cat.id, next);
      toast.success('Category renamed');
      setRenamingCat(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    }
  };

  const removeCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? Items in this category will move to Uncategorized.`)) return;
    try {
      await deleteFurnitureEssentialCategory(cat.id);
      toast.success('Category deleted');
      await load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const addEssentialToCategory = async (categoryName) => {
    const n = newEssentialName.trim();
    if (!n) return;
    try {
      const exists = entries.some(e => e.name.toLowerCase() === n.toLowerCase());
      if (exists) {
        toast.error('An essential with that name already exists');
        return;
      }
      const created = await upsertFurnitureEssentialLinks(n, {
        category: categoryName || null,
        notes: '',
        links: [],
      });
      setNewEssentialName('');
      await load();
      // Jump straight into the edit dialog so the user can add links /
      // notes / price without a second click.
      setEditing(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create essential');
    }
  };

  const moveEssentialToCategory = async (entry, newCategory) => {
    try {
      await upsertFurnitureEssentialLinks(entry.name, {
        category: newCategory || null,
        notes: entry.notes || '',
        links: entry.links || [],
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed');
    }
  };

  // Duplicate an essential. Finds the first available "<name> copy",
  // "<name> copy 2" … and opens the edit dialog on the new entry so the
  // user can tweak it immediately.
  const duplicateEssential = async (entry) => {
    const base = `${entry.name} copy`;
    const takenLower = new Set(entries.map(e => e.name.toLowerCase()));
    let candidate = base;
    let n = 2;
    while (takenLower.has(candidate.toLowerCase())) {
      candidate = `${base} ${n}`;
      n += 1;
    }
    try {
      const created = await upsertFurnitureEssentialLinks(candidate, {
        category: entry.category || null,
        notes: entry.notes || '',
        links: (entry.links || []).map(l => ({
          label: l.label || '',
          url: l.url || '',
          price: l.price == null ? null : l.price,
        })),
      });
      await load();
      setEditing(created);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Duplicate failed');
    }
  };

  if (loading) return <div className="p-6 text-sm text-[#5b6773]">Loading essentials…</div>;

  return (
    <div className="space-y-4" data-testid="essential-links-manager">
      <p className="text-sm text-[#5b6773]">
        Catalog of standard essential items. Click any name to edit its category, notes, and default purchase links.
        When an essential is added to a project room, the URLs auto-attach and the first link's price fills the listed-price column.
        {readOnly && (
          <span className="ml-1 inline-flex items-center gap-1 text-amber-700 font-medium">
            <Lock className="h-3 w-3" /> Read-only — admin access required to edit.
          </span>
        )}
      </p>

      {/* Top toolbar */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9ca3af]" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search essentials…"
            className="pl-8 h-9 text-sm"
            data-testid="ess-lib-search"
          />
        </div>
        {!readOnly && (
          <>
            <div className="flex items-center gap-1">
              <Input
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                placeholder="New category"
                className="h-9 text-sm w-44"
                data-testid="ess-lib-new-category"
              />
              <Button size="sm" className="h-9 bg-emerald-700 hover:bg-emerald-800" onClick={addCategory} disabled={!newCatName.trim()} data-testid="ess-lib-add-category">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <Input
                value={newEssentialName}
                onChange={(e) => setNewEssentialName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEssentialToCategory(null); } }}
                placeholder="New essential"
                className="h-9 text-sm w-48"
                data-testid="ess-lib-new-essential"
              />
              <Button size="sm" variant="outline" className="h-9" onClick={() => addEssentialToCategory(null)} disabled={!newEssentialName.trim()} data-testid="ess-lib-add-essential">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Category sections */}
      <div className="space-y-2">
        {grouped.length === 0 && (
          <Card><CardContent className="py-10 text-center text-sm text-[#9ca3af]">No essentials yet.</CardContent></Card>
        )}
        {grouped.map(group => {
          const isCollapsed = !!collapsed[group.key];
          const showSection = !filter || group.items.length > 0;
          if (!showSection) return null;
          return (
            <Card key={group.key} data-testid={`ess-lib-group-${group.key}`}>
              <div className="flex items-center gap-2 px-4 py-2 border-b bg-[#f3efe6]/30">
                <button
                  onClick={() => setCollapsed(c => ({ ...c, [group.key]: !c[group.key] }))}
                  className="text-[#5b6773] hover:text-[#1f2933]"
                  aria-label={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {renamingCat && renamingCat === group.id ? (
                  <Input
                    autoFocus
                    value={catNameDraft}
                    onChange={(e) => setCatNameDraft(e.target.value)}
                    onBlur={() => saveCatName(group)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                      if (e.key === 'Escape') setRenamingCat(null);
                    }}
                    className="h-7 text-sm w-48"
                  />
                ) : (
                  <h3 className="text-sm font-semibold text-[#1f2933]">{group.name}</h3>
                )}
                <span className="text-[11px] text-[#9ca3af]">{group.items.length}</span>

                {!readOnly && group.id && renamingCat !== group.id && (
                  <div className="ml-auto flex items-center gap-1">
                    <button
                      onClick={() => { setRenamingCat(group.id); setCatNameDraft(group.name); }}
                      className="text-[#5b6773]/70 hover:text-[#1f2933] p-1"
                      aria-label="Rename category"
                      data-testid={`ess-lib-cat-rename-${group.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => removeCategory(group)}
                      className="text-[#a8324a]/70 hover:text-[#a8324a] p-1"
                      aria-label="Delete category"
                      data-testid={`ess-lib-cat-delete-${group.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {!isCollapsed && (
                <CardContent className="p-2">
                  {group.items.length === 0 ? (
                    <p className="text-[11px] text-[#9ca3af] italic px-2 py-1">Empty.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-0.5">
                      {group.items.map(entry => (
                        <EssentialRow
                          key={entry.name_key}
                          entry={entry}
                          readOnly={readOnly}
                          onClick={() => !readOnly && setEditing(entry)}
                          categories={categories}
                          onMove={(catName) => moveEssentialToCategory(entry, catName)}
                          onDuplicate={() => duplicateEssential(entry)}
                        />
                      ))}
                    </div>
                  )}
                  {!readOnly && (
                    <div className="px-2 pt-2 mt-1 border-t">
                      <QuickAddInput
                        onAdd={async (v) => {
                          const exists = entries.some(x => x.name.toLowerCase() === v.toLowerCase());
                          if (exists) { toast.error('An essential with that name already exists'); return; }
                          const created = await upsertFurnitureEssentialLinks(v, {
                            category: group.isUncategorized ? null : group.name,
                            notes: '',
                            links: [],
                          });
                          await load();
                          setEditing(created);
                        }}
                        testId={`ess-lib-quick-add-${group.key}`}
                      />
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      <EssentialEditDialog
        open={!!editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        entry={editing}
        categories={categories}
        onChanged={load}
      />
    </div>
  );
}

function QuickAddInput({ onAdd, testId }) {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const v = val.trim();
    if (!v) return;
    setBusy(true);
    try { await onAdd(v); setVal(''); }
    finally { setBusy(false); }
  };
  return (
    <Input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
      onBlur={() => { if (val.trim()) submit(); }}
      disabled={busy}
      placeholder="+ Add essential here…"
      className="h-7 text-xs border-dashed"
      data-testid={testId}
    />
  );
}

function EssentialRow({ entry, onClick, readOnly, categories, onMove, onDuplicate }) {
  const linkCount = (entry.links || []).length;
  const hasNotes = !!(entry.notes && entry.notes.trim());

  return (
    <div
      className={`group flex items-center gap-1 px-2 py-1 rounded ${readOnly ? 'cursor-default' : 'cursor-pointer hover:bg-[#2d948c]/10'}`}
      onClick={onClick}
      data-testid={`ess-lib-row-${entry.name_key.replace(/\s+/g, '-')}`}
    >
      <span className="text-[12px] text-[#1f2933] truncate flex-1" title={hasNotes ? entry.notes : entry.name}>
        {entry.name}
      </span>
      {linkCount > 0 && (
        <span className="text-[10px] text-emerald-700/80" title={`${linkCount} link${linkCount === 1 ? '' : 's'}`}>
          <Link2 className="h-3 w-3" />
        </span>
      )}
      {hasNotes && (
        <span className="text-[10px] text-[#5b6773]/70" title={entry.notes}>
          <StickyNote className="h-3 w-3" />
        </span>
      )}
      {!readOnly && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
            className="text-[#5b6773]/70 hover:text-[#2d948c] p-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
            aria-label="Duplicate"
            title="Duplicate"
            data-testid={`ess-lib-duplicate-${entry.name_key.replace(/\s+/g, '-')}`}
          >
            <Copy className="h-3 w-3" />
          </button>
          <select
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onMove(e.target.value || null)}
            value={entry.category || ''}
            className="text-[10px] text-[#5b6773] bg-transparent border-0 opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none cursor-pointer max-w-[80px]"
            aria-label="Move to category"
            title="Move to category"
          >
            <option value="">— uncat —</option>
            {categories.map(c => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}
